import * as z from 'zod/v4';

/**
 * External-adapter gateway-key helpers live here rather than in
 * external-payment-adapter.ts: this module is bundled into the checkout
 * client, and a relative import between the two breaks one of NodeNext tsc,
 * the tsup DTS build, or Turbopack depending on the specifier. The server-only
 * adapter module re-exports these for its consumers.
 */
export const EXTERNAL_PAYMENT_ADAPTER_GATEWAY_PREFIX = 'EXTERNAL:' as const;

export type ExternalPaymentAdapterGatewayKey =
  `${typeof EXTERNAL_PAYMENT_ADAPTER_GATEWAY_PREFIX}${string}`;

const EXTERNAL_ADAPTER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function buildExternalPaymentAdapterGatewayKey(
  adapterId: string,
): ExternalPaymentAdapterGatewayKey | null {
  const normalizedId = adapterId.trim().toLowerCase();
  return EXTERNAL_ADAPTER_ID_PATTERN.test(normalizedId)
    ? `${EXTERNAL_PAYMENT_ADAPTER_GATEWAY_PREFIX}${normalizedId}`
    : null;
}

export function parseExternalPaymentAdapterGatewayKey(gateway: string): string | null {
  const trimmed = gateway.trim();
  if (!/^external:/i.test(trimmed)) {
    return null;
  }

  const adapterId = trimmed.slice(trimmed.indexOf(':') + 1).trim().toLowerCase();
  return EXTERNAL_ADAPTER_ID_PATTERN.test(adapterId) ? adapterId : null;
}

export function isExternalPaymentAdapterGatewayKey(
  gateway: string,
): gateway is ExternalPaymentAdapterGatewayKey {
  return parseExternalPaymentAdapterGatewayKey(gateway) !== null;
}

export const COIN_GATEWAY_KEYS = [
  'BITCOIN',
  'LITECOIN',
  'ETHEREUM',
  'BITCOIN_CASH',
  'MONERO',
  'RIPPLE',
  'TRON',
  'SOLANA',
  'POLYGON',
  'BINANCE_COIN',
  'CRONOS',
  'CONCORDIUM',
  'NANO',
  'DOGECOIN',
  'DASH',
] as const;

export const CRYPTO_PROVIDER_KEYS = [
  'NOWPAYMENTS',
  'CRYPTOMUS',
  'OXAPAY',
] as const;

export const MANAGED_CRYPTO_PROVIDER_KEYS = [
  'NOWPAYMENTS_WHITELABEL',
] as const;

export const GENERIC_STABLECOIN_GATEWAY_FAMILIES = {
  USDT: ['USDT_TRC20', 'USDT_ERC20', 'USDT_BEP20', 'USDT_POLYGON', 'USDT_SOL'],
  USDC: ['USDC_ERC20', 'USDC_TRC20', 'USDC_BEP20', 'USDC_POLYGON', 'USDC_SOL'],
  DAI: ['DAI_ERC20'],
} as const;

export const GENERIC_STABLECOIN_GATEWAY_KEYS = Object.keys(
  GENERIC_STABLECOIN_GATEWAY_FAMILIES,
) as Array<keyof typeof GENERIC_STABLECOIN_GATEWAY_FAMILIES>;

export const TOKEN_GATEWAY_KEYS = [
  ...GENERIC_STABLECOIN_GATEWAY_FAMILIES.USDT,
  ...GENERIC_STABLECOIN_GATEWAY_FAMILIES.USDC,
  ...GENERIC_STABLECOIN_GATEWAY_FAMILIES.DAI,
] as const;

export const CRYPTO_GATEWAY_KEYS = [
  ...COIN_GATEWAY_KEYS,
  ...TOKEN_GATEWAY_KEYS,
] as const;

export const LEGACY_CRYPTO_GATEWAY_KEYS = [
  'BITCOIN_LN',
] as const;

export const CRYPTO_PROVIDER_GATEWAY_KEYS = [
  ...CRYPTO_PROVIDER_KEYS,
  ...MANAGED_CRYPTO_PROVIDER_KEYS,
] as const;

export const CONCRETE_CRYPTO_GATEWAY_KEYS = [
  ...CRYPTO_GATEWAY_KEYS,
  ...LEGACY_CRYPTO_GATEWAY_KEYS,
] as const;

// Embed v1 predates Concordium support. This list is frozen by design so new
// shared-catalog coins cannot silently enter the embed-v1 session boundary.
export const EMBED_CRYPTO_GATEWAY_KEYS = [
  'BITCOIN',
  'LITECOIN',
  'ETHEREUM',
  'BITCOIN_CASH',
  'MONERO',
  'RIPPLE',
  'TRON',
  'SOLANA',
  'POLYGON',
  'BINANCE_COIN',
  'CRONOS',
  'NANO',
  'DOGECOIN',
  'DASH',
  'USDT_TRC20',
  'USDT_ERC20',
  'USDT_BEP20',
  'USDT_POLYGON',
  'USDT_SOL',
  'USDC_ERC20',
  'USDC_TRC20',
  'USDC_BEP20',
  'USDC_POLYGON',
  'USDC_SOL',
  'DAI_ERC20',
  'BITCOIN_LN',
] as const;

export type CoinGatewayKey = typeof COIN_GATEWAY_KEYS[number];
export type TokenGatewayKey = typeof TOKEN_GATEWAY_KEYS[number];
export type CryptoGatewayKey = typeof CRYPTO_GATEWAY_KEYS[number];
export type LegacyCryptoGatewayKey = typeof LEGACY_CRYPTO_GATEWAY_KEYS[number];
export type CryptoProviderKey = typeof CRYPTO_PROVIDER_KEYS[number];
export type ManagedCryptoProviderKey = typeof MANAGED_CRYPTO_PROVIDER_KEYS[number];
export type CryptoProviderGatewayKey = typeof CRYPTO_PROVIDER_GATEWAY_KEYS[number];

export const NOWPAYMENTS_DYNAMIC_GATEWAY_PREFIX = 'NOWPAYMENTS:' as const;
export type NowPaymentsDynamicGatewayKey = `${typeof NOWPAYMENTS_DYNAMIC_GATEWAY_PREFIX}${string}`;

const NOWPAYMENTS_CURRENCY_CODE_PATTERN = /^[A-Z0-9]{2,12}$/;

export function buildNowPaymentsDynamicGatewayKey(
  currencyCode: string,
): NowPaymentsDynamicGatewayKey | null {
  const normalizedCode = currencyCode.trim().toUpperCase();
  if (!NOWPAYMENTS_CURRENCY_CODE_PATTERN.test(normalizedCode)) {
    return null;
  }

  return `${NOWPAYMENTS_DYNAMIC_GATEWAY_PREFIX}${normalizedCode}`;
}

export function parseNowPaymentsDynamicGatewayKey(gateway: string): string | null {
  const normalized = gateway.trim().toUpperCase();
  if (!normalized.startsWith(NOWPAYMENTS_DYNAMIC_GATEWAY_PREFIX)) {
    return null;
  }

  const code = normalized.slice(NOWPAYMENTS_DYNAMIC_GATEWAY_PREFIX.length);
  return NOWPAYMENTS_CURRENCY_CODE_PATTERN.test(code) ? code.toLowerCase() : null;
}

export function isNowPaymentsDynamicGatewayKey(
  gateway: string,
): gateway is NowPaymentsDynamicGatewayKey {
  return parseNowPaymentsDynamicGatewayKey(gateway) !== null;
}

export const DEFAULT_NOWPAYMENTS_CRYPTO_GATEWAY_KEYS = CRYPTO_GATEWAY_KEYS;

export const DEFAULT_CRYPTOMUS_CRYPTO_GATEWAY_KEYS = [
  'BITCOIN',
  'LITECOIN',
  'ETHEREUM',
  'TRON',
  'SOLANA',
  'POLYGON',
  'BINANCE_COIN',
  'BITCOIN_CASH',
  'MONERO',
  'RIPPLE',
  'DOGECOIN',
  'USDT_TRC20',
  'USDT_ERC20',
  'USDT_BEP20',
  'USDT_POLYGON',
  'USDT_SOL',
  'USDC_ERC20',
  'USDC_BEP20',
  'USDC_POLYGON',
  'DAI_ERC20',
] as const;

export const DEFAULT_OXAPAY_CRYPTO_GATEWAY_KEYS = [
  'BITCOIN',
  'LITECOIN',
  'ETHEREUM',
  'TRON',
  'SOLANA',
  'POLYGON',
  'BINANCE_COIN',
  'BITCOIN_CASH',
  'MONERO',
  'RIPPLE',
  'DOGECOIN',
  'USDT_TRC20',
  'USDT_ERC20',
  'USDT_BEP20',
  'USDT_POLYGON',
  'USDC_ERC20',
] as const;

export const DEFAULT_PROVIDER_GATEWAY_SELECTIONS = {
  NOWPAYMENTS: DEFAULT_NOWPAYMENTS_CRYPTO_GATEWAY_KEYS,
  NOWPAYMENTS_WHITELABEL: DEFAULT_NOWPAYMENTS_CRYPTO_GATEWAY_KEYS,
  CRYPTOMUS: DEFAULT_CRYPTOMUS_CRYPTO_GATEWAY_KEYS,
  OXAPAY: DEFAULT_OXAPAY_CRYPTO_GATEWAY_KEYS,
} as const;

export const POPULAR_PLATFORM_MANAGED_CRYPTO_GATEWAY_KEYS = [
  'BITCOIN',
  'LITECOIN',
  'ETHEREUM',
  'USDT_TRC20',
  'USDT_ERC20',
] as const;

export const CHECKOUT_METHOD_ORDER_KEYS = [
  'STRIPE',
  'PAYPAL',
  'PAYPAL_FF',
  'MOLLIE',
  'AUTHORIZENET',
  'NMI',
  'SQUARE',
  'SUMUP',
  'SHOPIFY',
  'PANDABASE',
  'DEBLOMASSI',
  'SHOPPEXPAY',
  'MONEYMOTION',
  'OVGC',
  'WHOP',
  'DODO',
  'MAVERICK',
  'CASHAPP',
  'VENMO',
  'CRYPTO',
  'CUSTOMER_BALANCE',
  'EXTERNAL',
  'MANUAL',
] as const;

/**
 * Gateway implementations that remain in the codebase for historical payment
 * reads and webhook reconciliation, but cannot be configured or used for new
 * payments.
 */
export const DISABLED_PAYMENT_GATEWAY_KEYS = ['PANDABASE', 'STORRIK'] as const;

const DISABLED_PAYMENT_GATEWAY_KEY_SET = new Set<string>(
  DISABLED_PAYMENT_GATEWAY_KEYS,
);

export function isDisabledPaymentGateway(gateway: string): boolean {
  return DISABLED_PAYMENT_GATEWAY_KEY_SET.has(gateway.trim().toUpperCase());
}

/**
 * Gateway implementations that remain in the codebase but must not be
 * advertised or selectable in merchant and buyer UI.
 */
export const RETIRED_UI_PAYMENT_GATEWAY_KEYS = [
  'SHOPPEXPAY',
  ...DISABLED_PAYMENT_GATEWAY_KEYS,
] as const;

const RETIRED_UI_PAYMENT_GATEWAY_KEY_SET = new Set<string>(
  RETIRED_UI_PAYMENT_GATEWAY_KEYS,
);

export function isRetiredUiPaymentGateway(gateway: string): boolean {
  return RETIRED_UI_PAYMENT_GATEWAY_KEY_SET.has(gateway.trim().toUpperCase());
}

export const DEFAULT_PAYPAL_FF_MANAGED_IPN_URL = 'https://paypal-ff.myshoppex.io';

export type CheckoutMethodOrderKey = typeof CHECKOUT_METHOD_ORDER_KEYS[number];
export type WritableCheckoutMethodOrderKey = Exclude<
  CheckoutMethodOrderKey,
  typeof DISABLED_PAYMENT_GATEWAY_KEYS[number]
>;

export const WRITABLE_CHECKOUT_METHOD_ORDER_KEYS = CHECKOUT_METHOD_ORDER_KEYS.filter(
  (gateway): gateway is WritableCheckoutMethodOrderKey => !isDisabledPaymentGateway(gateway),
);

export interface StoredPaymentGatewayRow {
  provider?: string | null;
  is_active?: boolean | number | string | null;
  isActive?: boolean | number | string | null;
  external_id?: string | null;
  externalId?: string | null;
  has_credentials?: boolean | number | string | null;
  hasCredentials?: boolean | number | string | null;
  has_settings?: boolean | number | string | null;
  hasSettings?: boolean | number | string | null;
}

export interface OxapayCheckoutGatewayInfo {
  gateway_key?: string;
  gatewayKey?: string;
  display_name?: string;
  displayName?: string;
  symbol?: string;
  network?: string;
  network_name?: string | null;
  networkName?: string | null;
  required_confirmations?: number | null;
  requiredConfirmations?: number | null;
  deposit_min?: number | null;
  depositMin?: number | null;
  withdraw_min?: number | null;
  withdrawMin?: number | null;
  withdraw_fee?: number | null;
  withdrawFee?: number | null;
}

export interface PaymentGatewaySecretRef {
  set: boolean;
}

export interface PaymentGatewayHealth {
  status: 'READY' | 'NEEDS_ATTENTION';
  code?: string;
  message?: string;
  checked_at?: string;
}

export interface PaymentGatewayState {
  provider: string;
  type: string;
  enabled: boolean;
  connected: boolean;
  credentials: Record<string, PaymentGatewaySecretRef>;
  public_config: Record<string, unknown>;
  webhook?: Record<string, unknown>;
  health?: PaymentGatewayHealth;
}

export type GatewayIntegrationType =
  | 'OAUTH'
  | 'API_KEY'
  | 'SDK'
  | 'LEGACY'
  | 'DIRECT'
  | 'FORWARDING'
  | 'AGGREGATOR';

export interface NormalizedPaymentGatewayState {
  provider: string;
  id: string;
  type: string;
  name: string;
  enabled: boolean;
  connected: boolean;
  health_status?: 'READY' | 'NEEDS_ATTENTION';
  health_code?: string;
  health_message?: string;
  health_checked_at?: string;
  created_at: string;
  updated_at: string;
  integration_type?: 'SDK' | 'LEGACY';
  config?: Record<string, unknown>;
}

const COIN_GATEWAY_KEY_SET = new Set<string>(COIN_GATEWAY_KEYS);
const TOKEN_GATEWAY_KEY_SET = new Set<string>(TOKEN_GATEWAY_KEYS);
const CRYPTO_GATEWAY_KEY_SET = new Set<string>(CRYPTO_GATEWAY_KEYS);
const LEGACY_CRYPTO_GATEWAY_KEY_SET = new Set<string>(LEGACY_CRYPTO_GATEWAY_KEYS);
const CRYPTO_PROVIDER_KEY_SET = new Set<string>(CRYPTO_PROVIDER_KEYS);
const MANAGED_CRYPTO_PROVIDER_KEY_SET = new Set<string>(MANAGED_CRYPTO_PROVIDER_KEYS);
const CRYPTO_PROVIDER_GATEWAY_KEY_SET = new Set<string>(CRYPTO_PROVIDER_GATEWAY_KEYS);
const CONCRETE_CRYPTO_GATEWAY_KEY_SET = new Set<string>(CONCRETE_CRYPTO_GATEWAY_KEYS);
const EMBED_CRYPTO_GATEWAY_KEY_SET = new Set<string>(EMBED_CRYPTO_GATEWAY_KEYS);
const GENERIC_STABLECOIN_GATEWAY_KEY_SET = new Set<string>(GENERIC_STABLECOIN_GATEWAY_KEYS);
const CHECKOUT_METHOD_ORDER_KEY_SET = new Set<string>(CHECKOUT_METHOD_ORDER_KEYS);
const CHECKOUT_METHOD_GROUP_BY_GATEWAY: Record<string, CheckoutMethodOrderKey> = {
  STRIPE: 'STRIPE',
  PAYPAL: 'PAYPAL',
  PAYPAL_FF: 'PAYPAL_FF',
  MOLLIE: 'MOLLIE',
  AUTHORIZENET: 'AUTHORIZENET',
  NMI: 'NMI',
  SQUARE: 'SQUARE',
  SUMUP: 'SUMUP',
  SHOPIFY: 'SHOPIFY',
  PANDABASE: 'PANDABASE',
  DEBLOMASSI: 'DEBLOMASSI',
  SHOPPEXPAY: 'SHOPPEXPAY',
  MONEYMOTION: 'MONEYMOTION',
  OVGC: 'OVGC',
  WHOP: 'WHOP',
  DODO: 'DODO',
  MAVERICK: 'MAVERICK',
  CASH_APP: 'CASHAPP',
  VENMO: 'VENMO',
  CUSTOMER_BALANCE: 'CUSTOMER_BALANCE',
};

const LEGACY_GATEWAY_ALIASES: Record<string, string> = {
  CASHAPP: 'CASH_APP',
  PAYPAL_CREDIT_CARD: 'PAYPAL',
  EUTHEREUM: 'ETHEREUM',
  USDT_MATIC: 'USDT_POLYGON',
  USDC_MATIC: 'USDC_POLYGON',
};

function assertUniqueEntries(name: string, values: readonly string[]) {
  if (new Set(values).size !== values.length) {
    throw new Error(`${name} contains duplicate entries`);
  }
}

assertUniqueEntries('COIN_GATEWAY_KEYS', COIN_GATEWAY_KEYS);
assertUniqueEntries('TOKEN_GATEWAY_KEYS', TOKEN_GATEWAY_KEYS);
assertUniqueEntries('CRYPTO_GATEWAY_KEYS', CRYPTO_GATEWAY_KEYS);
assertUniqueEntries('LEGACY_CRYPTO_GATEWAY_KEYS', LEGACY_CRYPTO_GATEWAY_KEYS);
assertUniqueEntries('CRYPTO_PROVIDER_KEYS', CRYPTO_PROVIDER_KEYS);
assertUniqueEntries('MANAGED_CRYPTO_PROVIDER_KEYS', MANAGED_CRYPTO_PROVIDER_KEYS);
assertUniqueEntries('CRYPTO_PROVIDER_GATEWAY_KEYS', CRYPTO_PROVIDER_GATEWAY_KEYS);
assertUniqueEntries('DEFAULT_NOWPAYMENTS_CRYPTO_GATEWAY_KEYS', DEFAULT_NOWPAYMENTS_CRYPTO_GATEWAY_KEYS);
assertUniqueEntries('DEFAULT_CRYPTOMUS_CRYPTO_GATEWAY_KEYS', DEFAULT_CRYPTOMUS_CRYPTO_GATEWAY_KEYS);
assertUniqueEntries('DEFAULT_OXAPAY_CRYPTO_GATEWAY_KEYS', DEFAULT_OXAPAY_CRYPTO_GATEWAY_KEYS);

for (const key of CRYPTO_GATEWAY_KEYS) {
  if (!COIN_GATEWAY_KEY_SET.has(key) && !TOKEN_GATEWAY_KEY_SET.has(key)) {
    throw new Error(`CRYPTO_GATEWAY_KEYS contains an unclassified gateway: ${key}`);
  }
}

export function parseGatewayList(input: unknown): string[] {
  if (Array.isArray(input)) {
    return input.filter((entry): entry is string => typeof entry === 'string');
  }

  if (typeof input !== 'string') {
    return [];
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return [];
  }

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter((entry): entry is string => typeof entry === 'string');
      }
    } catch {
      // Fallback to comma-separated parsing.
    }
  }

  return trimmed
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function normalizeGatewayKey(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  if (/^manual:/i.test(trimmed)) {
    const manualId = trimmed.slice(trimmed.indexOf(':') + 1).trim();
    return manualId ? `MANUAL:${manualId}` : 'MANUAL';
  }

  if (/^external:/i.test(trimmed)) {
    const adapterId = parseExternalPaymentAdapterGatewayKey(trimmed);
    return adapterId
      ? `${EXTERNAL_PAYMENT_ADAPTER_GATEWAY_PREFIX}${adapterId}`
      : trimmed.toUpperCase();
  }

  let normalized = trimmed.toUpperCase();

  const stablecoinNetworkMatch = normalized.match(/^(USDT|USDC|DAI):([A-Z0-9_]+)$/);
  if (stablecoinNetworkMatch) {
    const [, asset, rawNetwork] = stablecoinNetworkMatch;
    const network = rawNetwork === 'MATIC' ? 'POLYGON' : rawNetwork;
    normalized = `${asset}_${network}`;
  }

  return LEGACY_GATEWAY_ALIASES[normalized] ?? normalized;
}

export function normalizeGatewayRestrictionValues(values: readonly string[]): string[] {
  const normalized = new Set<string>();

  for (const value of values) {
    const gateway = normalizeGatewayKey(value);
    if (gateway) {
      normalized.add(gateway);
    }
  }

  return [...normalized];
}

export function normalizeGatewayRestrictionInput(input: unknown): string[] | null {
  const normalized = normalizeGatewayRestrictionValues(parseGatewayList(input));
  return normalized.length > 0 ? normalized : null;
}

export function isValidProductGatewaySelection(input: string): boolean {
  const normalized = normalizeGatewayKey(input);
  if (
    !normalized
    || normalized === 'NULL'
    || normalized === 'UNDEFINED'
    || isDisabledPaymentGateway(normalized)
  ) {
    return false;
  }

  return normalized === 'CUSTOMER_BALANCE'
    || normalized === 'CRYPTO'
    || normalized === 'EXTERNAL'
    || parseExternalPaymentAdapterGatewayKey(normalized) !== null
    || isFinanceGateway(normalized)
    || isCryptoProviderGatewayKey(normalized)
    || isConcreteCryptoGatewayKey(normalized)
    || isGenericStablecoinGatewayKey(normalized);
}

export function findInvalidProductGatewaySelections(input: unknown): string[] {
  return normalizeGatewayRestrictionValues(parseGatewayList(input))
    .filter((gateway) => !isValidProductGatewaySelection(gateway));
}

/**
 * Canonical persisted product restrictions. External write paths should reject
 * `invalid`; trusted repair/import paths may deliberately keep only `gateways`.
 */
export function partitionProductGatewaySelections(input: unknown): {
  gateways: string[];
  invalid: string[];
} {
  const normalized = normalizeGatewayRestrictionValues(parseGatewayList(input));
  const gateways: string[] = [];
  const invalid: string[] = [];

  for (const gateway of normalized) {
    (isValidProductGatewaySelection(gateway) ? gateways : invalid).push(gateway);
  }

  return { gateways, invalid };
}

export const FINANCE_GATEWAY_KEYS = [
  'STRIPE', 'PAYPAL', 'PAYPAL_FF', 'SKRILL', 'CASHAPP', 'CASH_APP', 'PERFECT_MONEY',
  'SQUARE', 'SUMUP', 'SHOPIFY', 'PANDABASE', 'DEBLOMASSI', 'SHOPPEXPAY', 'MONEYMOTION', 'OVGC', 'WHOP',
  'DODO', 'MAVERICK', 'VENMO', 'MOLLIE', 'AUTHORIZENET', 'NMI',
] as const;

const FINANCE_GATEWAY_KEY_SET = new Set<string>(
  FINANCE_GATEWAY_KEYS.map((gateway) => normalizeGatewayKey(gateway)),
);

export function isFinanceGateway(gateway: string): boolean {
  const normalized = normalizeGatewayKey(gateway);
  return normalized === 'MANUAL'
    || normalized.startsWith('MANUAL:')
    || normalized === 'EXTERNAL'
    || parseExternalPaymentAdapterGatewayKey(normalized) !== null
    || FINANCE_GATEWAY_KEY_SET.has(normalized);
}

export const VALID_PRODUCT_PAYMENT_GATEWAY_RESTRICTION_MODES = ['USE_STORE_DEFAULT', 'CUSTOM'] as const;

export type ProductPaymentGatewayRestrictionMode =
  (typeof VALID_PRODUCT_PAYMENT_GATEWAY_RESTRICTION_MODES)[number];

export function normalizeProductPaymentGatewayRestrictionMode(
  value: unknown,
): ProductPaymentGatewayRestrictionMode {
  return value === 'CUSTOM' ? 'CUSTOM' : 'USE_STORE_DEFAULT';
}

export function isProductGatewayRestrictionPublishable(
  mode: unknown,
  gateways: unknown,
): boolean {
  return normalizeProductPaymentGatewayRestrictionMode(mode) !== 'CUSTOM'
    || normalizeGatewayRestrictionInput(gateways) !== null;
}

export function isCoinGateway(gateway: string): gateway is CoinGatewayKey {
  return COIN_GATEWAY_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isTokenGateway(gateway: string): gateway is TokenGatewayKey {
  return TOKEN_GATEWAY_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isCryptoGateway(gateway: string): gateway is CryptoGatewayKey {
  return CRYPTO_GATEWAY_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isProviderKey(gateway: string): gateway is CryptoProviderKey {
  return CRYPTO_PROVIDER_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isManagedCryptoProviderKey(gateway: string): gateway is ManagedCryptoProviderKey {
  return MANAGED_CRYPTO_PROVIDER_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isCryptoProviderGatewayKey(gateway: string): boolean {
  return CRYPTO_PROVIDER_GATEWAY_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isGenericStablecoinGatewayKey(gateway: string): boolean {
  return GENERIC_STABLECOIN_GATEWAY_KEY_SET.has(normalizeGatewayKey(gateway));
}

export function isConcreteCryptoGatewayKey(gateway: string): boolean {
  const normalizedGateway = normalizeGatewayKey(gateway);
  return isCryptoGateway(normalizedGateway)
    || LEGACY_CRYPTO_GATEWAY_KEY_SET.has(normalizedGateway)
    || CONCRETE_CRYPTO_GATEWAY_KEY_SET.has(normalizedGateway)
    || isNowPaymentsDynamicGatewayKey(normalizedGateway);
}

export function isEmbedCryptoGatewayKey(gateway: string): boolean {
  // Exact-match on the frozen embed-v1 key set, NOT normalizeGatewayKey():
  // alias/colon rewriting (USDT:ERC20, USDT_MATIC, EUTHEREUM, …) would widen
  // the embed crypto boundary and auto-start crypto sessions for stored
  // gateway values the embed flow never accepted before.
  const exactGateway = gateway.trim().toUpperCase();
  return EMBED_CRYPTO_GATEWAY_KEY_SET.has(exactGateway)
    || isNowPaymentsDynamicGatewayKey(exactGateway);
}

/**
 * True for anything that resolves to a crypto payment, in every shape a gateway
 * key can take: a concrete coin (`BITCOIN`, `USDT:ERC20`), a generic stablecoin
 * whose chain the buyer still has to choose (`USDT`), and a provider key that
 * expands into a coin list (`OXAPAY`, `NOWPAYMENTS`, `CRYPTOMUS`).
 *
 * Use this — not `isConcreteCryptoGatewayKey` on its own — wherever the question
 * is "may this be selected without the buyer choosing it?". Selecting crypto
 * locks an exchange rate against a deposit address, so a caller that only
 * recognises concrete coins lets a provider key through and starts that session
 * anyway. Both halves of that mistake have been shipped before.
 */
export function isCryptoPaymentSelection(gateway: string): boolean {
  return isConcreteCryptoGatewayKey(gateway)
    || isGenericStablecoinGatewayKey(gateway)
    || isCryptoProviderGatewayKey(gateway);
}

export function getConcreteCryptoGatewaySelections(gateways: Iterable<string>): string[] {
  const selected = new Set<string>();

  for (const gateway of gateways) {
    const normalized = normalizeGatewayKey(gateway);
    if (isConcreteCryptoGatewayKey(normalized)) {
      selected.add(normalized);
    }
  }

  return [...selected];
}

/**
 * External labels of crypto payment rails as emitted by the invoice read model
 * (resolveExternalCryptoRailLabel): WHITE_LABEL for managed provider rails and
 * NATIVE for self-hosted BTC/LTC nodes. These are rail labels, not gateway or
 * processor keys.
 */
export const CRYPTO_RAIL_GATEWAY_LABELS = ['WHITE_LABEL', 'NATIVE'] as const;
export type CryptoRailGatewayLabel = typeof CRYPTO_RAIL_GATEWAY_LABELS[number];
const CRYPTO_RAIL_GATEWAY_LABEL_SET = new Set<string>(CRYPTO_RAIL_GATEWAY_LABELS);

/**
 * `PaymentProvider` enum value persisted for the self-hosted crypto rail. The
 * read model emits the 'NATIVE' rail label, while invoice and attempt rows carry
 * the provider value itself — both name the same rail.
 */
export const NATIVE_CRYPTO_PROVIDER_KEY = 'NATIVE_CRYPTO' as const;

/** Processor keys persisted on crypto payment attempts (providers plus the NATIVE rail). */
export const CRYPTO_PAYMENT_PROCESSOR_KEYS = [...CRYPTO_PROVIDER_GATEWAY_KEYS, 'NATIVE'] as const;
export type CryptoPaymentProcessorKey = typeof CRYPTO_PAYMENT_PROCESSOR_KEYS[number];
const CRYPTO_PAYMENT_PROCESSOR_KEY_SET = new Set<string>(CRYPTO_PAYMENT_PROCESSOR_KEYS);

export function isCryptoRailGatewayLabel(value: string): value is CryptoRailGatewayLabel {
  return CRYPTO_RAIL_GATEWAY_LABEL_SET.has(normalizeGatewayKey(value));
}

export function isCryptoPaymentProcessorKey(value: string): value is CryptoPaymentProcessorKey {
  return CRYPTO_PAYMENT_PROCESSOR_KEY_SET.has(normalizeGatewayKey(value));
}

/**
 * Single source of truth for "does this invoice gateway value mean crypto":
 * concrete coin/token keys (incl. legacy), generic stablecoin families,
 * provider gateway keys, and external rail labels. Consumers classifying
 * invoice rows by their public `gateway` value must use this instead of
 * maintaining local lists.
 */
export function isCryptoInvoiceGatewayLabel(value: string): boolean {
  const normalized = normalizeGatewayKey(value);
  return isConcreteCryptoGatewayKey(normalized)
    || GENERIC_STABLECOIN_GATEWAY_KEY_SET.has(normalized)
    || CRYPTO_PROVIDER_GATEWAY_KEY_SET.has(normalized)
    || CRYPTO_RAIL_GATEWAY_LABEL_SET.has(normalized)
    || normalized === NATIVE_CRYPTO_PROVIDER_KEY;
}

function getAvailableManualGatewaySelections(gateways: Iterable<string>): string[] {
  const selected = new Set<string>();

  for (const gateway of gateways) {
    const normalized = normalizeGatewayKey(gateway);
    if (normalized === 'MANUAL' || normalized.startsWith('MANUAL:')) {
      selected.add(normalized);
    }
  }

  return [...selected];
}

function getAvailableExternalAdapterSelections(gateways: Iterable<string>): string[] {
  const selected = new Set<string>();

  for (const gateway of gateways) {
    const normalized = normalizeGatewayKey(gateway);
    if (normalized === 'EXTERNAL' || parseExternalPaymentAdapterGatewayKey(normalized) !== null) {
      selected.add(normalized);
    }
  }

  return [...selected];
}

function getProviderScopedGatewaySelections(
  providerGateway: keyof typeof DEFAULT_PROVIDER_GATEWAY_SELECTIONS,
  availableGateways: string[],
): string[] {
  const providerDefaults = DEFAULT_PROVIDER_GATEWAY_SELECTIONS[providerGateway];

  if (availableGateways.length === 0) {
    return [...providerDefaults];
  }

  const availableConcreteGateways = new Set(getConcreteCryptoGatewaySelections(availableGateways));
  return providerDefaults.filter((gateway) => availableConcreteGateways.has(gateway));
}

export function expandGatewaySelection(
  gateway: string,
  options?: { availableGateways?: Iterable<string> },
): string[] {
  const normalized = normalizeGatewayKey(gateway);
  if (!normalized) {
    return [];
  }

  const availableGateways = options?.availableGateways
    ? [...options.availableGateways].map((entry) => normalizeGatewayKey(entry)).filter(Boolean)
    : [];

  if (normalized === 'MANUAL') {
    return availableGateways.length > 0
      ? getAvailableManualGatewaySelections(availableGateways)
      : ['MANUAL'];
  }

  if (normalized.startsWith('MANUAL:')) {
    return [normalized];
  }

  if (normalized === 'EXTERNAL') {
    return availableGateways.length > 0
      ? getAvailableExternalAdapterSelections(availableGateways)
      : ['EXTERNAL'];
  }

  if (parseExternalPaymentAdapterGatewayKey(normalized) !== null) {
    return [normalized];
  }

  if (normalized.startsWith(EXTERNAL_PAYMENT_ADAPTER_GATEWAY_PREFIX)) {
    return [];
  }

  if (normalized === 'CRYPTO') {
    return availableGateways.length > 0
      ? getConcreteCryptoGatewaySelections(availableGateways)
      : [...CRYPTO_GATEWAY_KEYS];
  }

  if (isCryptoProviderGatewayKey(normalized)) {
    return getProviderScopedGatewaySelections(
      normalized as keyof typeof DEFAULT_PROVIDER_GATEWAY_SELECTIONS,
      availableGateways,
    );
  }

  if (isGenericStablecoinGatewayKey(normalized)) {
    const concreteGateways =
      GENERIC_STABLECOIN_GATEWAY_FAMILIES[
        normalized as keyof typeof GENERIC_STABLECOIN_GATEWAY_FAMILIES
      ];

    if (availableGateways.length > 0) {
      const availableSet = new Set(availableGateways);
      return concreteGateways.filter((entry) => availableSet.has(entry));
    }

    return [...concreteGateways];
  }

  return [normalized];
}

export function resolveConcreteGatewaySelections(
  input: unknown,
  options?: { availableGateways?: Iterable<string> },
): string[] | null {
  const normalized = normalizeGatewayRestrictionInput(input);
  if (!normalized) {
    return null;
  }

  const resolved = new Set<string>();
  for (const gateway of normalized) {
    for (const expanded of expandGatewaySelection(gateway, options)) {
      resolved.add(expanded);
    }
  }

  return [...resolved];
}

export function normalizeCheckoutMethodOrder(
  input: Iterable<string> | null | undefined,
): CheckoutMethodOrderKey[] {
  if (!input) {
    return [];
  }

  const normalized = new Set<CheckoutMethodOrderKey>();

  for (const entry of input) {
    if (typeof entry !== 'string') {
      continue;
    }

    const candidate = entry.trim().toUpperCase();
    if (CHECKOUT_METHOD_ORDER_KEY_SET.has(candidate)) {
      normalized.add(candidate as CheckoutMethodOrderKey);
    }
  }

  return [...normalized];
}

export function resolveCheckoutMethodOrder(
  input: Iterable<string> | null | undefined,
): CheckoutMethodOrderKey[] {
  const normalized = normalizeCheckoutMethodOrder(input);
  const resolved = [...normalized];

  for (const key of CHECKOUT_METHOD_ORDER_KEYS) {
    if (!resolved.includes(key)) {
      resolved.push(key);
    }
  }

  return resolved;
}

export function getCheckoutMethodOrderGroup(
  gateway: string,
): CheckoutMethodOrderKey | null {
  const normalized = normalizeGatewayKey(gateway);
  if (!normalized) {
    return null;
  }

  if (normalized === 'MANUAL' || normalized.startsWith('MANUAL:')) {
    return 'MANUAL';
  }

  if (normalized === 'EXTERNAL' || parseExternalPaymentAdapterGatewayKey(normalized) !== null) {
    return 'EXTERNAL';
  }

  if (isCryptoProviderGatewayKey(normalized) || isConcreteCryptoGatewayKey(normalized)) {
    return 'CRYPTO';
  }

  return CHECKOUT_METHOD_GROUP_BY_GATEWAY[normalized] ?? null;
}

export function sortGatewaysByCheckoutMethodOrder(
  gateways: Iterable<string>,
  input: Iterable<string> | null | undefined,
): string[] {
  const explicitOrder = normalizeCheckoutMethodOrder(input);
  if (explicitOrder.length === 0) {
    return [...gateways];
  }

  const resolvedOrder = resolveCheckoutMethodOrder(explicitOrder);
  const orderIndex = new Map(
    resolvedOrder.map((key, index) => [key, index] as const),
  );

  return [...gateways].sort((left, right) => {
    const leftGroup = getCheckoutMethodOrderGroup(left);
    const rightGroup = getCheckoutMethodOrderGroup(right);
    const leftIndex = leftGroup ? (orderIndex.get(leftGroup) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
    const rightIndex = rightGroup ? (orderIndex.get(rightGroup) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;

    if (leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }

    return 0;
  });
}

export function getCheckoutMethodOrderKeysForGateways(
  gateways: Iterable<string>,
  input?: Iterable<string> | null,
): CheckoutMethodOrderKey[] {
  const orderedGroups = new Set<CheckoutMethodOrderKey>();

  for (const gateway of sortGatewaysByCheckoutMethodOrder(gateways, input)) {
    const group = getCheckoutMethodOrderGroup(gateway);
    if (group) {
      orderedGroups.add(group);
    }
  }

  return [...orderedGroups];
}

export function getCheckoutMethodOrderLabel(key: CheckoutMethodOrderKey): string {
  switch (key) {
    case 'STRIPE':
      return 'Credit / Debit Cards';
    case 'PAYPAL':
      return 'PayPal';
    case 'PAYPAL_FF':
      return 'PayPal F&F';
    case 'MOLLIE':
      return 'Mollie';
    case 'AUTHORIZENET':
      return 'Authorize.net';
    case 'NMI':
      return 'NMI';
    case 'SQUARE':
      return 'Square';
    case 'SUMUP':
      return 'SumUp';
    case 'SHOPIFY':
      return 'Shopify';
    case 'PANDABASE':
      return 'Pandabase';
    case 'DEBLOMASSI':
      return 'DebloMassi';
    case 'SHOPPEXPAY':
      return 'Card payment';
    case 'MONEYMOTION':
      return 'MoneyMotion';
    case 'OVGC':
      return 'OVGC Payments';
    case 'WHOP':
      return 'Whop';
    case 'DODO':
      return 'Dodo Payments';
    case 'MAVERICK':
      return 'Maverick Payments';
    case 'CASHAPP':
      return 'Cash App';
    case 'VENMO':
      return 'Venmo';
    case 'CRYPTO':
      return 'Crypto';
    case 'CUSTOMER_BALANCE':
      return 'Store Credit';
    case 'EXTERNAL':
      return 'External Providers';
    case 'MANUAL':
      return 'Manual Payment Methods';
  }
}

export const SecretRefSchema = z.object({
  set: z.boolean(),
});

export const PaymentGatewayHealthSchema = z.object({
  status: z.enum(['READY', 'NEEDS_ATTENTION']).optional(),
  code: z.string().optional(),
  message: z.string().optional(),
  checked_at: z.string().optional(),
});

export const PaymentGatewayStateSchema = z.object({
  provider: z.string(),
  type: z.string(),
  enabled: z.boolean(),
  connected: z.boolean(),
  credentials: z.record(z.string(), SecretRefSchema),
  public_config: z.record(z.string(), z.unknown()),
  webhook: z.record(z.string(), z.unknown()).optional(),
  health: PaymentGatewayHealthSchema.optional(),
});

export type PaymentGatewayStateFromSchema = z.infer<typeof PaymentGatewayStateSchema>;
